import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '../ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import AthleteAvatar from '../guardian/AthleteAvatar';
import AthleteForm from '../guardian/AthleteForm';
import { guardianshipsService, type HeldWard } from '../../services/guardianships';
import { cn } from '../../lib/utils';
import type { AvatarColor } from '../../lib/avatar-colors';
import type { StageParticipation } from '../../types/trophy';

/** Who a tap on "enter" can be about: the viewer, or one of their children. */
export interface PickableAthlete {
  /** Null for the viewer themselves — the entry endpoint wants no `for_user_id`. */
  user_id: string | null;
  full_name: string;
  name?: string | null;
  surname?: string | null;
  birth_date?: string | null;
  avatar_color?: string | null;
  avatar_url?: string | null;
}

interface ChooseAthleteDialogProps {
  /** The stage being entered; null keeps the dialog closed. */
  stage: StageParticipation | null;
  trophyName: string;
  /** Only those who can still be entered — see useStageEntry. */
  athletes: PickableAthlete[];
  /** The address the entries will be under, so the parent knows which account. */
  email: string;
  entering: boolean;
  onCancel: () => void;
  onConfirm: (athlete: PickableAthlete) => void;
  /** A child who did not exist a moment ago, so the caller can keep the list. */
  onWardCreated: (ward: HeldWard) => void;
  onWardUpdated: (ward: HeldWard) => void;
}

function birthYear(value?: string | null): string {
  if (!value) return '';
  return value.slice(0, 4);
}

/**
 * "Who are you entering?" — the one question a parent has to answer that a
 * climber entering themselves never sees.
 *
 * **Two cards in view at the narrowest**, picked by swiping or by tapping: one
 * at a time reads as "here is your athlete" rather than "choose one", and a
 * parent cannot see that there is somebody beside them without scrolling. A
 * card is a face, a name and a year — a managed account has no picture, so the
 * avatar is a colour the parent chose, and tapping it changes that colour
 * there and then, the only place in the app where a ward's appearance is
 * theirs to set. Whoever is already on the start list is not here at all:
 * there is nothing to decide about them.
 *
 * Adding a child is the last card, and it is a **card with a button**, not the
 * form itself: cards in a row are as tall as the tallest, so a form in one of
 * them made every athlete sit in a half-empty box the height of a phone.
 * Pressing it swaps the whole dialog for the form, with the way back.
 *
 * Picking somebody is not the entry yet: the entry dialog follows for whoever
 * was picked (EnterStageDialog), because a child needs a gender, a class and
 * possibly a turn as much as anybody — and that is where they are asked.
 */
export default function ChooseAthleteDialog({
  stage,
  trophyName,
  athletes,
  email,
  entering,
  onCancel,
  onConfirm,
  onWardCreated,
  onWardUpdated,
}: ChooseAthleteDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(0);
  const [adding, setAdding] = useState(false);

  // The "add" slide sits after the athletes, so its index is their count.
  const addIndex = athletes.length;

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // A fresh question every time it opens: whoever was picked last time is not
  // an answer to this stage.
  useEffect(() => {
    if (!stage) {
      setSelected(0);
      setAdding(false);
    }
  }, [stage]);

  const current = selected < athletes.length ? athletes[selected] : null;

  // Both ways of choosing end here: the carousel is told to move, and the
  // selection is set whether or not it can.
  const pick = useCallback(
    (index: number) => {
      setSelected(index);
      api?.scrollTo(index);
    },
    [api]
  );

  const dateLabel = useMemo(() => {
    if (!stage?.start_date) return '';
    return new Date(stage.start_date).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [stage?.start_date, i18n.language]);

  const changeColor = useCallback(
    async (athlete: PickableAthlete, color: AvatarColor) => {
      if (!athlete.user_id) return;
      try {
        onWardUpdated(await guardianshipsService.updateWard(athlete.user_id, { avatar_color: color }));
      } catch {
        toast({ title: t('guardian.picker.colorError'), variant: 'destructive' });
      }
    },
    [onWardUpdated, t, toast]
  );

  // A child who did not exist a moment ago takes the slide the "add" card was
  // on, because they are almost certainly the answer to the question.
  const addedAthlete = (ward: HeldWard) => {
    onWardCreated(ward);
    setAdding(false);
    pick(addIndex);
  };

  return (
    <Dialog open={!!stage} onOpenChange={(open) => !open && !entering && onCancel()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden p-4 sm:max-w-lg sm:p-6">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="text-base sm:text-lg">
            {adding
              ? t('guardian.picker.addTitle')
              : t('guardian.picker.title', { stage: stage?.name ?? '' })}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {adding
              ? t('guardian.picker.addDescription')
              : t('guardian.picker.description', {
                  trophy: trophyName,
                  stage: stage?.name ?? '',
                  date: dateLabel,
                })}
          </DialogDescription>
        </DialogHeader>

        {adding ? (
          // The form's own cancel is the way back; a second control saying the
          // same thing under it was just noise.
          <AthleteForm
            idPrefix="new-athlete"
            onSaved={addedAthlete}
            onCancel={() => setAdding(false)}
            cancelLabel={t('guardian.picker.backToAthletes')}
          />
        ) : (
          <>
            {/* The arrows get a gutter of their own (`px-7` here, `left-0`
                there): shadcn puts them at -left-12, which on a phone is off
                the dialog and over the page behind it. */}
            <Carousel setApi={setApi} opts={{ align: 'start' }} className="relative w-full min-w-0 px-7">
              <CarouselContent
                role="radiogroup"
                aria-label={t('guardian.picker.title', { stage: stage?.name ?? '' })}
              >
                {athletes.map((athlete, index) => (
                  <CarouselItem key={athlete.user_id ?? 'self'} className="basis-1/2 sm:basis-1/3">
                    <div
                      // Swiping to an athlete picks them, and so does tapping
                      // one: the selection is React's own, so a carousel that
                      // has not measured itself yet still leaves the parent
                      // able to choose.
                      role="radio"
                      aria-checked={selected === index}
                      tabIndex={0}
                      onClick={() => pick(index)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          pick(index);
                        }
                      }}
                      className={cn(
                        'flex h-full cursor-pointer flex-col items-center gap-2 rounded-lg border px-2 py-3 text-center transition',
                        selected === index && 'ring-2 ring-primary'
                      )}
                      data-testid="athlete-slide"
                      data-athlete={athlete.user_id ?? 'self'}
                    >
                      <AthleteAvatar
                        name={athlete.name ?? athlete.full_name}
                        surname={athlete.surname}
                        color={athlete.avatar_color}
                        imageUrl={athlete.avatar_url}
                        size="md"
                        // Only a ward's colour is the parent's to set; their
                        // own avatar is a profile matter, with a picture.
                        onColorChange={
                          athlete.user_id && !athlete.avatar_url
                            ? (color) => changeColor(athlete, color)
                            : undefined
                        }
                      />
                      <div className="w-full min-w-0">
                        <p className="truncate text-sm font-medium leading-tight">{athlete.full_name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[birthYear(athlete.birth_date), !athlete.user_id ? t('guardian.picker.you') : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}

                <CarouselItem className="basis-1/2 sm:basis-1/3">
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="flex h-full w-full cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed px-2 py-3 text-center transition hover:bg-accent"
                    data-testid="add-athlete-slide"
                  >
                    <span className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Plus className="size-6" aria-hidden />
                    </span>
                    <span className="text-sm font-medium leading-tight">{t('guardian.picker.addTitle')}</span>
                  </button>
                </CarouselItem>
              </CarouselContent>

              {/* One athlete and the add card is not a carousel worth steering. */}
              {athletes.length > 0 && (
                <>
                  <CarouselPrevious type="button" className="left-0 size-7" />
                  <CarouselNext type="button" className="right-0 size-7" />
                </>
              )}
            </Carousel>

            {/* What the parent needs to know once, not in the title: where the
                organiser writes, and who settles class and fee. */}
            <p className="break-words text-xs text-muted-foreground">
              {t('guardian.picker.footnote', { email })}
            </p>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={entering}
                className="w-full sm:w-auto"
              >
                {t('common.cancel')}
              </Button>
              {current ? (
                <Button
                  type="button"
                  disabled={entering}
                  onClick={() => onConfirm(current)}
                  data-testid="confirm-athlete-entry"
                  className="w-full min-w-0 sm:w-auto"
                >
                  {entering && <Loader2 className="mr-2 size-4 shrink-0 animate-spin" />}
                  <span className="truncate">
                    {t('guardian.picker.continue', { name: current.full_name })}
                  </span>
                </Button>
              ) : (
                // The add card is the slide in view: the button does what the
                // card does rather than sitting there disabled.
                <Button type="button" onClick={() => setAdding(true)} className="w-full gap-1.5 sm:w-auto">
                  <Plus className="size-4" aria-hidden />
                  {t('guardian.picker.addTitle')}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
